<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ user.userName }}
      </portal>

      <card :title="$t('admin.pageUser.card.general')" cols="12">
        <hm-input v-model="user.userName"
                  :lazy="true"
                  :disabled="user.deleted || isSelf || !isEmpty(user.lastLogin)"
                  :label="$t('admin.pageUser.userName')"
                  :parent-errors="errors"
                  name="user.userName"
                  cols="12 6@desktop 4@hd"
                  @blur="$v.$touch()"/>

        <hm-input :lazy="true"
                  v-model="user.email"
                  :label="$t('admin.pageUser.email')"
                  :disabled="user.deleted"
                  :parent-errors="errors"
                  cols="12 6@desktop 4@hd"
                  name="user.email"
                  @blur="$v.$touch()" />

        <hm-multiselect id="roles"
                        v-model="user.roles"
                        :options="roles"
                        :disabled="user.deleted || isSelf"
                        :label="$t('admin.pageUser.roles')"
                        cols="12 6@desktop 4@hd"
                        name="inheritRestrictions"
                        label-expression="${option.name}" />

        <hm-input-toggle v-model="user.enabled"
                         :disabled="user.deleted || isSelf"
                         :label="$t('admin.pageUser.enabled')"
                         :enabled-title="$t('enabled')"
                         :disabled-title="$t('disabled')"
                         cols="12 6@desktop 4@hd"
                         name="enabled"/>

        <hm-multiselect v-if="user.roles.includes('ROLE_UNIT_OWNER')"
                        id="hotelRoomAccessRights"
                        v-model="user.hotelRoomAccessRights"
                        :options="hotelSettings.rooms.all(user.hotelRoomAccessRights).filter(it => !it.deleted).map(it => ({ id: it.roomNumber, name: it.roomNumber }))"
                        :disabled="isSelf"
                        :label="$t('admin.pageUser.hotelRoomAccessRights')"
                        name="hotelRoomAccessRights"
                        cols="12 6@desktop 4@hd"/>

        <!-- ADD NEW FIELDS ABOVE -->

        <hm-input :lazy="true"
                  v-model="user.lastLogin"
                  :label="$t('admin.pageUser.lastLogin')"
                  :disabled="true"
                  cols="start-1 12 6@desktop 4@hd"
                  name="lastLogin" />

        <hm-input :lazy="true"
                  v-model="user.lastPasswordChange"
                  :label="$t('admin.pageUser.lastPasswordChange')"
                  :disabled="true"
                  :parent-errors="errors"
                  cols="12 6@desktop 4@hd"
                  name="lastPasswordChange" />

        <hm-input ref="password"
                  :lazy="true"
                  v-model="password"
                  :label="$t('admin.pageUser.password')"
                  :disabled="user.deleted"
                  type="password"
                  cols="12 6@desktop 4@hd start-1"
                  name="password"
                  @blur="$v.$touch()"/>

        <hm-input ref="passwordConfirm"
                  :lazy="true"
                  v-model="passwordConfirm"
                  :label="$t('admin.pageUser.passwordConfirm')"
                  :disabled="user.deleted"
                  :parent-errors="errors"
                  type="password"
                  cols="12 6@desktop 4@hd"
                  name="passwordConfirm"
                  @blur="$v.$touch()" />
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!user.deleted && !isEmpty(user.email)"
                            :label="$t('admin.pageUser.actions.requestPasswordReset')"
                            icon="undo"
                            color="orange"
                            @click="requestPasswordReset(false)" />
        <hm-floating-button v-if="!user.deleted && canDelete"
                            :label="$t('admin.pageUser.actions.deleteUser')"
                            icon="delete"
                            color="error"
                            @click="deleteUser(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import ValidateMixin from '../../../mixins/ValidateMixin';
import HmInputSelect from '../../lib/inputs/HmInputSelect';

export default {
  name: 'PageUser',
  components: { HmInputSelect, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'user',
      variable: 'user',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.user.deleted === true) return;

        this.$v.$touch();
        if (this.$v.$anyError) {
          cbSaveComplete();
          return;
        }

        const toSave = cloneDeep(this.user);

        this.axios.put(this.serviceUrls.user.get(this, this.userId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('user.list');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  validations() {
    return {
      user: {
        userName: this.$hv__createValidators('required|regex:^[a-zA-Z0-9]+$|min_length:3', this.$t('admin.pageUser.userName')),
        email: this.$hv__createValidators('email', this.$t('admin.pageUser.email')),
      },
      // password: this.$hv__createValidators('sameAs:passwordConfirm', this.$t('admin.pageUser.password')),
      passwordConfirm: this.$hv__createValidators('same_as:password', this.$t('admin.pageUser.passwordConfirm')),
    };
  },

  mixins: [HotelmeisterMixin, SaveWatchMixin, ValidateMixin],

  data() {
    return {
      user: {
      },
      userId: this.$route.params.userId,
      dataLoaded: false,
      password: null,
      passwordConfirm: null,
    };
  },

  computed: {
    isSelf: function () {
      // if current user is not yet loaded - assume it's him - just to be safe
      return isNil(this.user) || isNil(this.user.id) ? true : this.user.id === this.$root.user.id;
    },
    canDelete: function () {
      return !this.isSelf;
    },
    roles: function () {
      const roles = [
        'ROLE_USER',
        'ROLE_RESERVATION_REQUEST',
        'ROLE_RESERVATION_NOTIFICATION',
        'ROLE_INVOICING',
        'ROLE_RATE_ADMIN',
        'ROLE_INTERFACES',
        'ROLE_FINANCIAL_REPORTS',
        'ROLE_USER_ADMIN',
        'ROLE_ADMIN',
        'ROLE_WEB_AGENCY',
        'ROLE_UNIT_OWNER',
        'ROLE_SYSADMIN',
        'ROLE_HOTELMEISTER_SUPER_ADMIN',
      ];

      return roles
        .filter((it) => {
          switch (it) {
            case 'ROLE_HOTELMEISTER_SUPER_ADMIN':
              if (!isNil(this.user.roles) && this.user.roles.includes('ROLE_HOTELMEISTER_SUPER_ADMIN')) return true;
              if (['system', 'hotelmeister'].includes(this.hotelSettings.tenant.subdomain.toLowerCase())) return true;
              return false;
            case 'ROLE_SYSADMIN':
              if (!isNil(this.user.roles) && this.user.roles.includes('ROLE_SYSADMIN')) return true;
              if (this.hasRole('ROLE_SYSADMIN') && (this.hotelSettings.tenant.id === this.$root.user.tenantId)) return true;
              return false;
            default:
              return true;
          }
        })
        .filter((it) => {
          switch (it) {
            case 'ROLE_UNIT_OWNER':
              return this.hasLicense('ApartmentOwnerLogin');
            case 'ROLE_INVOICING':
              return this.hasLicense('Invoicing');
            default:
              return true;
          }
        })
        .map(it => ({
          id: it,
          name: this.$t(`ApplicationUserRole.${it}`),
        }));
    },
  },

  watch: {
    user: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
    password: function () {
      if (this.password === this.passwordConfirm) {
        this.user.password = this.password;
      }
    },
    passwordConfirm() {
      if (this.password === this.passwordConfirm) {
        this.user.password = this.password;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('user.list');

    const $vm = this;

    // this.$validator.extend('passwordsMatch', {
    //   getMessage(field, val) {
    //     return 'Passwörter stimmen nicht überein.';
    //   },
    //   validate(value, field) {
    //     return $vm.password === $vm.passwordConfirm;
    //   },
    // });
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.user.get(this, this.userId))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.user = response.data.data;
            this.dataLoaded = true;
          }
        });
    },
    deleteUser(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.user.get(this, this.userId))
            .then((response) => {
              this.dataLoaded = false;
              this.user = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('user.list');
              this.$router.push({
                name: 'users',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageUser.confirmDeleteUser'),
            (doNotShowAgain) => {
              this.deleteUser(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageUser.userInUse'));
      }
    },
    requestPasswordReset() {
      this.axios.post(this.serviceUrls.user.requestPasswordReset(this, this.userId))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          this.showMessages(error.response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageUser.vue